import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, Skeleton } from "@mui/material";

import useIsOwn from "../../../../../hooks/useIsOwn";

import { Service } from "../../../types/service.type";
import { GO_SERVICE_QUERY_KEYS } from "../../../api/queryKeys";
import { useJoinProgram } from "../../../api/mutations/useJoinProgram";
import { StripePayModal, useStripePayModal } from "../../../components/StripePayModal";
import { IdeallyFinishesInIcon, NumberOfVideosIcon, ProgramLevelIcon } from "../../../assets/icons/program-request";
import {
  SummaryHeader,
  SummaryHeaderLoader,
  SummaryInfo,
  SummaryInfoWithLoader,
  SummaryInfoWithChips,
  SummaryProviderDetails,
  SummaryProviderDetailsLoader,
  SummaryShare,
  SummaryShareLoader,
} from "../../../components/Summary";

export const ProgramSummary = ({
  programService,
  isProgramServiceLoading,
}: {
  programService?: Service;
  isProgramServiceLoading?: boolean;
}) => {
  const { t } = useTranslation("goService", { keyPrefix: "programPage" });
  const { t: tProgramLevel } = useTranslation("goService", { keyPrefix: "programLevel" });

  const canRequestService = programService?.canRequest;

  const { isOwn: isOwnService, isLoading: isOwnServiceLoading } = useIsOwn({
    userId: programService?.user?.id,
    isUserDataLoading: false,
  });

  const { openStripePayModal, handleOpenStripePayModal, handleCloseStripePayModal, clientSecret, publishableKey } =
    useStripePayModal();

  const { mutate: joinProgram, isPending: isJoinProgramPending } = useJoinProgram(programService?.id!, {
    onSuccess: (data) => {
      const { clientSecret, publishableKey } = data.transaction || {};
      if (clientSecret && publishableKey) {
        handleOpenStripePayModal(clientSecret, publishableKey);
      }
    },
  });

  const isLoading = isOwnServiceLoading || isProgramServiceLoading;

  if (isLoading) {
    return <ProgramSummaryLoader />;
  }

  if (!programService) return null;

  const { user } = programService;
  const { currency, amount } = programService.paymentConfig;

  const programDetails = [
    {
      icon: <IdeallyFinishesInIcon />,
      title: t("ideallyFinishesIn"),
      value: programService.idealCompletionTime,
    },
    {
      icon: <ProgramLevelIcon />,
      title: t("programLevel"),
      value: tProgramLevel(programService?.level ?? "beginner"),
    },
    {
      icon: <NumberOfVideosIcon />,
      title: t("numberOfVideos"),
      value: t("video", { count: programService.numberOfVideos }),
    },
  ];

  const handleJoinProgram = () => {
    if (!programService?.id) return;
    joinProgram();
  };

  return (
    <Box sx={styles.container}>
      <SummaryHeader
        amount={amount}
        currency={currency}
        type={programService.type}
        expiryDate={programService.paymentConfig.expiryDate}
      />
      <Divider />
      <Box sx={styles.summaryDetails}>
        {programDetails.map(({ icon, title, value }) => (
          <Fragment key={title}>
            {Array.isArray(value) && (
              <SummaryInfoWithChips icon={icon} title={title} firstItemName={value[0]} totalLength={value.length} />
            )}
            {!Array.isArray(value) && <SummaryInfo icon={icon} title={title} value={value} />}
          </Fragment>
        ))}
      </Box>
      <Divider />
      <SummaryProviderDetails user={user!} isProvider={!isOwnService} />
      <Divider />
      {!isOwnService && canRequestService && (
        <>
          <Button onClick={handleJoinProgram} disabled={isLoading || isJoinProgramPending} variant="primary-md">
            {t("joinThisProgram")}
          </Button>
          <StripePayModal
            open={openStripePayModal}
            closeModal={handleCloseStripePayModal}
            clientSecret={clientSecret}
            publishableKey={publishableKey}
            queryKey={
              [...GO_SERVICE_QUERY_KEYS.service(programService?.id!), { serviceId: programService?.id! }] as string[]
            }
          />
          <Divider />
        </>
      )}
      <SummaryShare title={t("shareThisProgram")} />
    </Box>
  );
};

export const ProgramSummaryLoader = () => {
  const { t } = useTranslation("goService", { keyPrefix: "programPage" });

  return (
    <Box sx={styles.container}>
      <SummaryHeaderLoader />
      <Divider />
      <SummaryInfoWithLoader icon={<IdeallyFinishesInIcon />} title={t("ideallyFinishesIn")} />
      <SummaryInfoWithLoader icon={<ProgramLevelIcon />} title={t("programLevel")} />
      <SummaryInfoWithLoader icon={<NumberOfVideosIcon />} title={t("numberOfVideos")} />
      <Divider />
      <SummaryProviderDetailsLoader />
      <Divider />
      <Skeleton variant="rounded" width="100%" height={44} />
      <Divider />
      <SummaryShareLoader />
    </Box>
  );
};

const styles = {
  container: {
    position: "sticky",
    top: "10px",
    display: "flex",
    width: "300px",
    padding: "24px 16px",
    flexDirection: "column",
    gap: "24px",
    borderRadius: "8px",
    border: "1px solid #E7E7E7",
    background: "var(--Gray-White, #FFF)",
    height: "fit-content",
  },
  summaryDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
} as const;
